import React, { createContext, useState, useEffect, useRef } from 'react';
import A4_MM_DIMENSION from '../../../enums/printPageA4SizeEnum';
import './styles.css';

const insertAtIndex = (arr, index, item) => {
  return [...arr.slice(0, index), item, ...arr.slice(index)];
};

const replaceAtIndex = (arr, index, item) => {
  return [...arr.slice(0, index), item, ...arr.slice(index + 1)];
};

function arraysAreEqual(array1, array2) {
  if (array1.length !== array2.length) {
    return false;
  }
  for (var i = 0; i < array1.length; i++) {
    if (array1[i] !== array2[i]) {
      return false;
    }
  }
  return true;
}

const getNextSections = (children = []) => {
  return React.Children.map(children, (child) =>
    child
      ? {
          id: child.props.id,
          name: child.props.name,
          sections: [],
          pageNumber: null,
        }
      : null,
  ).filter((item) => item !== null);
};

/**
 * Document serves as a wrapper for the DocumentContext. It keeps track of
 * the document's sections and pages, and provides methods to add, delete and update these sections and pages.
 *
 * @param {Object} props - The properties passed to the component.
 * @param {React.ReactNode} props.children - The child elements to be wrapped in the DocumentContext.
 *
 * @returns {React.Element} The DocumentContext.Provider element with the DocumentReScaler component
 * and children wrapped inside.
 *
 * @example
 *  <Document>
 *    <Section id='section1'/>
 *    <Section id='section2'/>
 *  </Document>
 *
 * The Document component provides the following context values:
 * - pages: an array of the current pages.
 * - sections: an array of the current sections.
 * - addPage: a function that takes a page object and adds it to the pages array.
 * - deletePage: a function that takes a page id and removes the corresponding page from the pages array.
 * - updateSections: a function that takes a section object and updates the corresponding section in the sections array.
 */
export const DocumentContext = createContext({
  pages: [],
  sections: [],
  addPage: () => {},
  updateSections: () => {},
  deleteSection: () => {},
});

export function Document({ children, ...props }) {
  const [sections, setSections] = useState([]);
  const [pages, setPages] = useState([]);

  const addPage = ({ id, root, overflow }) => {
    setPages((prevPages) => {
      const rootIndex = prevPages.findIndex((page) => page.root === root);
      if (rootIndex === -1) {
        return [...prevPages, { id, root: root, overflow: overflow }];
      } else {
        // Update existing page with new id
        if (overflow === 0) {
          return replaceAtIndex(prevPages, rootIndex, { id, root, overflow });
        } else {
          return insertAtIndex(prevPages, rootIndex + overflow, { id, root, overflow });
        }
      }
    });
  };

  const deletePage = (id) => {
    setPages((prevPages) => {
      return prevPages.filter((page) => page.id !== id);
    });
  };

  const updateSections = ({ id, ...attrs }) => {
    setSections((prevSections) =>
      prevSections.map((section) => (section.id === id ? { ...section, ...attrs } : section)),
    );
  };

  useEffect(() => {
    if (!children || children.length === 0) return;

    // Map the new and old arrays to ids only
    let nextSections = getNextSections(children);
    const prevIds = sections.map((child) => child.id);
    const nextIds = nextSections.map((child) => child.id);

    if (!arraysAreEqual(prevIds, nextIds)) {
      const sectionMap = new Map(sections.map((section) => [section.id, section]));

      nextSections = nextSections.map((nextSection) => {
        const matchingPrevSection = sectionMap.get(nextSection.id);
        return matchingPrevSection ? { ...nextSection, ...matchingPrevSection } : nextSection;
      });

      setSections(nextSections);
    }
  }, [children]);

  const contextValue = {
    pages,
    sections,
    addPage,
    deletePage,
    updateSections,
  };

  return (
    <DocumentReScaler {...props}>
      <DocumentContext.Provider value={contextValue}>
        {React.Children.map(children, (child, index) => (
          <div key={index} className="canvas-item">
            {child}
          </div>
        ))}
      </DocumentContext.Provider>
    </DocumentReScaler>
  );
}

/**
 * DocumentReScaler resizes its children to maintain a constant relative size
 * regardless of window size. This is achieved by listening to window's resize event and updating a scaling factor
 * accordingly. The scaling factor is then applied to the dimensions of the children to keep their size relative to
 * the window's size.
 *
 * @param {Object} props - The properties passed to the component.
 * @param {React.ReactNode} props.children - The child elements to be scaled.
 * @param {string} [props.id='canvas-scrollable'] - The id attribute of the wrapping div.
 * @param {number} props.scale - The initial scale factor. If not provided, defaults to 1 and uses local state.
 * @param {function} props.setScale - The function to update the scale factor. If not provided, uses a local state updater.
 *
 * @returns {React.Element} A div containing the scaled children.
 *
 * @example
 *  <DocumentReScaler scale={0.8} setScale={setScale}>
 *    <ChildComponent />
 *  </DocumentReScaler>
 */

export function DocumentReScaler({
  children,
  id = 'canvas-scrollable',
  scale: parentScale = null,
  setScale: setParentScale = null,
  ...props
}) {
  const canvasRef = useRef(null);
  const scaleRef = useRef(null);

  const [localScale, setLocalScale] = useState(1);
  const [scaledStyling, setScaledStyling] = useState({});

  // Use the passed state and setState if provided, otherwise use local state and setState
  const scale = parentScale ? parentScale : localScale;
  const setScale = setParentScale ? setParentScale : setLocalScale;

  useEffect(() => {
    const handleResize = () => {
      const canvasWidth = canvasRef.current.clientWidth;
      const A4Width = A4_MM_DIMENSION.width * (96 / 25.4);
      const newScale = Math.min((canvasWidth - 100) / (A4Width * 1.5), 1);
      setScale(newScale);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    if (scaleRef.current) {
      const newHeight = scaleRef.current.scrollHeight * scale + 100;
      const scaledStyling = {
        width: 'auto',
        gap: '20px',
        marginBottom: '100px',
        transformOrigin: 'top center',
        height: `${newHeight}px`,
        transform: `scale(${scale})`,
      };
      setScaledStyling(scaledStyling);
    }
  }, [scale, scaleRef]);

  return (
    <div id={id} ref={canvasRef} className="canvas-container" {...props}>
      <div
        id="scaled-wrapper"
        ref={scaleRef}
        className="scaled-wrapper print-scale-reset"
        style={scaledStyling}
      >
        {children}
      </div>
    </div>
  );
}
